// ============================================================
// Summit League v2 - Auth Context
// ============================================================

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import axiosClient from '../api/axiosClient';

interface User {
  userId: number;
  username: string;
  email: string;
  role: 'user' | 'moderator' | 'admin';
  avatarUrl?: string;
  emailVerified: boolean;
  tosAcceptedVersion: string | null;
  currentTosVersion: string;
  ageConfirmed: boolean;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (user: User, remember?: boolean) => void;
  logout: () => void;
  updateUser: (user: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const STORAGE_KEY = 'sl_auth';

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load auth state from storage on mount, then validate cookie with backend
  useEffect(() => {
    let cancelled = false;

    // Hydrate user info from storage for immediate UI (avoids flash)
    try {
      const stored = localStorage.getItem(STORAGE_KEY) || sessionStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Schema validation: ensure parsed data has expected shape before using.
        // This guards against corrupted or tampered localStorage values.
        if (
          parsed.user &&
          typeof parsed.user === 'object' &&
          typeof parsed.user.userId === 'number' &&
          typeof parsed.user.username === 'string' &&
          typeof parsed.user.emailVerified === 'boolean' &&
          ['user', 'moderator', 'admin'].includes(parsed.user.role)
        ) {
          // Note: role is stored for UI display only (e.g. showing admin panel link).
          // All role-based authorization is enforced server-side on every request.
          // Tampering with role in localStorage has no security impact.
          setUser(parsed.user);
        } else {
          // Invalid shape — clear corrupted data
          localStorage.removeItem(STORAGE_KEY);
          sessionStorage.removeItem(STORAGE_KEY);
        }
      }
    } catch {
      localStorage.removeItem(STORAGE_KEY);
      sessionStorage.removeItem(STORAGE_KEY);
    }

    // Validate cookie and fetch authoritative user data from backend.
    // This handles cross-subdomain auth: localStorage is per-origin so it
    // may be empty on a new subdomain, but the cookie is shared.
    axiosClient.get('/me')
      .then((res) => {
        if (cancelled) return;
        const u = res.data?.data?.user;
        if (u) {
          const mappedUser: User = {
            userId: u.user_id,
            username: u.username,
            email: u.email,
            role: u.role,
            avatarUrl: u.avatar_url || undefined,
            emailVerified: u.email_verified,
            tosAcceptedVersion: u.tos_accepted_version ?? null,
            currentTosVersion: u.current_tos_version,
            ageConfirmed: u.age_confirmed ?? false,
          };
          setUser(mappedUser);
          // Persist non-sensitive fields for future hydration (email excluded)
          const { email: _e, ...safeHydration } = mappedUser;
          localStorage.setItem(STORAGE_KEY, JSON.stringify({ user: safeHydration }));
        }
        setIsLoading(false);
      })
      .catch(() => {
        // Cookie invalid or missing — clear local state
        if (!cancelled) {
          setUser(null);
          localStorage.removeItem(STORAGE_KEY);
          sessionStorage.removeItem(STORAGE_KEY);
          setIsLoading(false);
        }
      });

    return () => { cancelled = true; };
  }, []);

  const login = useCallback((userData: User, remember = true) => {
    setUser(userData);

    // Store only non-sensitive fields in localStorage to reduce XSS exposure.
    // Email is intentionally excluded — it's fetched from the backend on load.
    const { email: _email, ...safeData } = userData;
    const storage = remember ? localStorage : sessionStorage;
    storage.setItem(STORAGE_KEY, JSON.stringify({ user: safeData }));

    // Clear the other storage
    (remember ? sessionStorage : localStorage).removeItem(STORAGE_KEY);
  }, []);

  const logout = useCallback(() => {
    // Call backend to clear the cookie
    axiosClient.post('/logout').catch(() => {
      // Best-effort — clear local state regardless
    });

    setUser(null);
    localStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(STORAGE_KEY);
  }, []);

  const updateUser = useCallback((updates: Partial<User>) => {
    setUser((prev) => {
      if (!prev) return prev;
      const updated = { ...prev, ...updates };

      // Persist only non-sensitive fields
      const { email: _email, ...safeData } = updated;
      const stored = localStorage.getItem(STORAGE_KEY) || sessionStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        const storage = localStorage.getItem(STORAGE_KEY) ? localStorage : sessionStorage;
        storage.setItem(STORAGE_KEY, JSON.stringify({ ...parsed, user: safeData }));
      }

      return updated;
    });
  }, []);

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuthContext() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
